import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const BondCalculator = () => {
  const [bondData, setBondData] = useState({
    faceValue: '',
    couponRate: '',
    yearsToMaturity: '',
    yieldToMaturity: '',
    purchasePrice: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBondData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateBondPrice = () => {
    const { faceValue, couponRate, yearsToMaturity, yieldToMaturity, purchasePrice } = bondData;

    if (!faceValue || !couponRate || !yearsToMaturity || !yieldToMaturity || !purchasePrice) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const FV = parseFloat(faceValue);
      const CR = parseFloat(couponRate) / 100;
      const YTM = parseFloat(yieldToMaturity) / 100;
      const N = parseFloat(yearsToMaturity);
      const PP = parseFloat(purchasePrice);

      if (isNaN(FV) || isNaN(CR) || isNaN(YTM) || isNaN(N) || isNaN(PP)) {
        setError('Please enter valid numbers for all fields');
        return;
      }

      if (FV <= 0 || CR < 0 || YTM < 0 || N <= 0 || PP <= 0) {
        setError('All values must be greater than zero');
        return;
      }

      const C = FV * CR;
      const bondPrice = (C / YTM) * (1 - Math.pow(1 + YTM, -N)) + (FV / Math.pow(1 + YTM, N));

      const generatePriceCurve = () => {
        const timePoints = Array.from({ length: 10 }, (_, i) => i * (N / 9));
        const theoreticalPrices = timePoints.map(t => 
          (C / YTM) * (1 - Math.pow(1 + YTM, -t)) + (FV / Math.pow(1 + YTM, t))
        );

        return {
          labels: timePoints,
          datasets: [
            {
              label: 'Theoretical Bond Price Curve',
              data: theoreticalPrices,
              borderColor: 'rgb(75, 192, 192)',
              tension: 0.1,
              fill: false
            }
          ]
        };
      };

      const resultsData = {
        faceValue: FV,
        couponRate: CR * 100,
        yearsToMaturity: N,
        yieldToMaturity: YTM * 100,
        purchasePrice: PP,
        bondPrice: bondPrice.toFixed(2),
        optimizationTips: getOptimizationTips(bondData)
      };

      setResults(resultsData);
      setChartData(generatePriceCurve());

    } catch (error) {
      setError('An error occurred while calculating bond price');
    }
  };

  const getOptimizationTips = (bondData) => {
    const tips = [];

    tips.push(
      'Diversify your bond portfolio to reduce risk',
      'Regularly review and rebalance your investments',
      'Consider long-term investment strategies',
      'Monitor market trends and adjust your portfolio accordingly',
      'Consult with a financial advisor for personalized advice'
    );

    return tips;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-green-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Bond Calculator | Investment Analysis Tool</title>
          <meta name="description" content="Calculate bond prices and analyze bond investments with our advanced calculator. Analyze investment performance and receive optimization tips." />
          <meta name="keywords" content="bond calculator, investment returns, investment analysis, portfolio optimization" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb- max-w-2xl mx-auto">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">💰</div>
            <h1 className="text-3xl font-bold text-gray-800">Bond Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>The Bond Calculator helps you analyze bond investment performance and provides:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Bond price calculations</li>
              <li>Optimization recommendations</li>
            </ul>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Face Value:</label>
              <input
                type="number"
                name="faceValue"
                value={bondData.faceValue}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter face value"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Rate (%):</label>
              <input
                type="number"
                name="couponRate"
                value={bondData.couponRate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter coupon rate"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Years to Maturity:</label>
              <input
                type="number"
                name="yearsToMaturity"
                value={bondData.yearsToMaturity}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter years to maturity"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Yield to Maturity (%):</label>
              <input
                type="number"
                name="yieldToMaturity"
                value={bondData.yieldToMaturity}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter yield to maturity"
              />
            </div>

            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Price:</label>
              <input
                type="number"
                name="purchasePrice"
                value={bondData.purchasePrice}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Enter purchase price"
              />
            </div>
          </div>

          <button
            onClick={calculateBondPrice}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Bond Price
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Bond Analysis</h2>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Bond Price</h3>
                  <p className="text-xl font-bold">{results.bondPrice}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Yield to Maturity</h3>
                  <p className="text-xl font-bold">{results.yieldToMaturity}%</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Line
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Bond Price'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Years'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Theoretical Bond Price Curve'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Optimization Tips</h3>
                <ul className="list-disc pl-6 space-y-2">
                  {results.optimizationTips.map((tip, index) => (
                    <li key={index} className="text-gray-600">{tip}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Understanding Bond Returns Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Bond Returns <span className="text-blue-600">📊</span></h2>
          <div className="text-gray-600">
            <p>Bond returns are crucial for evaluating the performance of your bond investments. They help in:</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Assessing the yield of your bonds</li>
              <li>Comparing different bond options</li>
              <li>Planning for financial goals</li>
              <li>Making informed investment decisions</li>
            </ul>
            <p className="italic mt-4">Note: Diversification and regular reviews are key to optimizing returns.</p>
          </div>
        </div>

        {/* How to Use Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use Bond Calculator <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Bond Calculator effectively:</p>
            
            <div className="step-section mt-4">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 1: Collect Your Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Record the face value of the bond</li>
                <li>Record the coupon rate of the bond</li>
                <li>Note the years to maturity of the bond</li>
                <li>Note the yield to maturity of the bond</li>
                <li>Note the purchase price of the bond</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 2: Enter Data</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Input the face value of the bond</li>
                <li>Enter the coupon rate of the bond</li>
                <li>Enter the years to maturity of the bond</li>
                <li>Enter the yield to maturity of the bond</li>
                <li>Enter the purchase price of the bond</li>
                <li>Click the "Calculate Bond Price" button</li>
              </ul>
            </div>

            <div className="step-section mt-6">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Step 3: Analyze Results</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Review the calculated bond price</li>
                <li>Check the theoretical bond price curve</li>
                <li>Follow optimization tips for improvement</li>
              </ul>
            </div>

            <div className="step-section mt-6 bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-600 mb-2">Important Considerations</h3>
              <ul className="list-disc pl-6 mt-2">
                <li>Consider the impact of interest rates on bond prices</li>
                <li>Monitor market trends and adjust your portfolio accordingly</li>
                <li>Regularly review and rebalance your investments</li>
                <li>Consult with a financial advisor for personalized advice</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Features <span className="text-blue-600">🚀</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li>Calculate bond prices based on face value, coupon rate, years to maturity, and yield to maturity</li>
              <li>Visualize bond price over time with a graph</li>
              <li>Receive optimization tips for better portfolio management</li>
              <li>Supports detailed analysis for deeper insights</li>
            </ul>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Benefits <span className="text-green-600">🌟</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li>Make informed investment decisions</li>
              <li>Optimize your bond portfolio for better returns</li>
              <li>Track the performance of your bond investments over time</li>
              <li>Receive personalized tips for portfolio improvement</li>
            </ul>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">FAQ <span className="text-yellow-600">❓</span></h2>
          <div className="text-gray-600">
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">How do I calculate my bond investment returns?</summary>
              <p className="mt-2">Enter the face value, coupon rate, years to maturity, yield to maturity, and purchase price of the bond. The calculator will then compute the bond price and provide a theoretical price curve over time.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">What is bond price?</summary>
              <p className="mt-2">Bond price is the current market value of a bond. It is influenced by factors such as interest rates, time to maturity, and the bond's coupon rate.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">What is yield to maturity?</summary>
              <p className="mt-2">Yield to maturity (YTM) is the total return anticipated on a bond if it is held until it matures. YTM accounts for both the bond's coupon payments and any capital gains or losses.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">How can I optimize my bond portfolio?</summary>
              <p className="mt-2">The calculator provides optimization tips such as diversifying your bond portfolio, regularly reviewing and rebalancing your investments, and consulting with a financial advisor.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">What if I don't know the purchase price of my bond?</summary>
              <p className="mt-2">If you don't know the purchase price of your bond, you can estimate it based on current market conditions or use historical data to make an educated guess.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">Can I use this calculator for different types of bonds?</summary>
              <p className="mt-2">Yes, this calculator can be used for various types of bonds, including government bonds, corporate bonds, and municipal bonds. Just ensure you have the correct face value, coupon rate, years to maturity, yield to maturity, and purchase price.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">How often should I review my bond portfolio?</summary>
              <p className="mt-2">It's generally recommended to review your bond portfolio at least once a year. However, significant market changes or personal financial events may warrant more frequent reviews.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">What is the impact of interest rates on bond prices?</summary>
              <p className="mt-2">Interest rates have an inverse relationship with bond prices. When interest rates rise, bond prices fall, and vice versa. This calculator helps you understand how changes in interest rates can affect your bond investments.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">Can I save my calculations for future reference?</summary>
              <p className="mt-2">Currently, this calculator does not support saving calculations. However, you can take screenshots or write down the results for future reference.</p>
            </details>
            <details className="mb-4">
              <summary className="font-semibold text-blue-600 cursor-pointer">Is this calculator suitable for long-term investment analysis?</summary>
              <p className="mt-2">Yes, this calculator can be used for long-term investment analysis. It provides bond prices and theoretical price curves, which are useful for comparing bonds over different periods.</p>
            </details>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BondCalculator;